import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import { Input } from 'antd';
import api from '../services/api';
import type { LocationPayload } from '../types/location';
import { cartoVoyagerLeafletTileUrl } from '../utils/cartoBasemap';

import 'leaflet/dist/leaflet.css';

const MAP_HEIGHT_PX = 320;
const mapShellStyle: React.CSSProperties = {
  width: '100%',
  height: MAP_HEIGHT_PX,
};
// Vietnam approximate centroid - default when no location
const defaultCenter: [number, number] = [14.0583, 108.2772];

/** Gọi trước mọi chỗ truyền tọa độ cho Leaflet — tránh NaN, string, blank. */
function parseCoord(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : null;
  }
  const s = String(v).trim();
  if (s === '' || s === 'NaN') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function isValidLatLngPair(
  a: number | null | undefined,
  b: number | null | undefined
): boolean {
  if (a == null || b == null) return false;
  return Number.isFinite(a) && Number.isFinite(b);
}

/** Bộ đôi tọa độ an toàn cho bản đồ (Leaflet) — luôn trả số hữu hạn hoặc default VN. */
function toMapCenterTuple(a: unknown, b: unknown): [number, number] {
  const la = parseCoord(a);
  const lo = parseCoord(b);
  if (la != null && lo != null && Math.abs(la) <= 90 && Math.abs(lo) <= 180) {
    return [la, lo];
  }
  return [defaultCenter[0], defaultCenter[1]];
}

function toMarkerPosition(
  a: number | null | undefined,
  b: number | null | undefined
): [number, number] | null {
  if (!isValidLatLngPair(a, b)) return null;
  const la = a as number;
  const lo = b as number;
  if (Math.abs(la) > 90 || Math.abs(lo) > 180) return null;
  return [la, lo];
}

// Fix default marker icon in react-leaflet (webpack issue)
const createIcon = () =>
  new L.Icon.Default({
    iconRetinaUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });

export interface LocationPickerProps {
  initialAddress?: string;
  initialLat?: number;
  initialLng?: number;
  onChange: (payload: LocationPayload) => void;
  onAddressChange?: (address: string) => void;
  countryRestriction?: 'vn';
  /**
   * Trong Modal/slide: bản đồ đợi vài trăm ms mới mount để container có kích thước thật (tránh Leaflet unproject → NaN).
   */
  deferMapMs?: number;
}

const MIN_MAP_PX = 8;

/** Gọi invalidateSize sau mount — tránh vùng xám khi container vừa hiện. */
function MapInvalidateOnMount({ layoutReady }: { layoutReady: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (!layoutReady) return;
    const run = () => {
      try {
        map.invalidateSize({ animate: false });
      } catch {
        /* ignore */
      }
    };
    run();
    const t1 = window.setTimeout(run, 100);
    const t2 = window.setTimeout(run, 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [map, layoutReady]);
  return null;
}

/**
 * Đồng bộ center/zoom từ state React sang Leaflet (MapContainer của react-leaflet chỉ đọc center/zoom 1 lần lúc tạo).
 * Gọi invalidateSize trước; bỏ qua khi kích thước map = 0 (tránh unproject → NaN).
 */
function MapViewSync({
  center,
  zoom,
  layoutReady,
}: {
  center: [number, number];
  zoom: number;
  /** true khi container DOM đã có kích thước thật (ResizeObserver) */
  layoutReady: boolean;
}) {
  const map = useMap();
  const last = useRef<{ la: number; lo: number; z: number } | null>(null);
  useEffect(() => {
    if (!layoutReady) {
      return;
    }
    const c = toMapCenterTuple(center[0], center[1]);
    const z = Math.min(20, Math.max(1, Math.round(Number(zoom))));
    if (
      !Number.isFinite(c[0]) ||
      !Number.isFinite(c[1]) ||
      !Number.isFinite(z)
    ) {
      return;
    }
    const prev = last.current;
    if (prev && prev.la === c[0] && prev.lo === c[1] && prev.z === z) {
      return;
    }

    const timeouts: number[] = [];
    const run = (attempt: number) => {
      if (attempt > 25) {
        return;
      }
      try {
        const s = map.getSize();
        if (s.x < MIN_MAP_PX || s.y < MIN_MAP_PX) {
          const tid = window.setTimeout(() => run(attempt + 1), 80);
          timeouts.push(tid);
          return;
        }
        map.invalidateSize({ animate: false });
        map.setView(c, z, { animate: false });
        last.current = { la: c[0], lo: c[1], z };
      } catch {
        const tid = window.setTimeout(() => run(attempt + 1), 80);
        timeouts.push(tid);
      }
    };
    const raf = requestAnimationFrame(() => {
      run(0);
      const tid = window.setTimeout(() => run(0), 200);
      timeouts.push(tid);
    });
    return () => {
      cancelAnimationFrame(raf);
      timeouts.forEach(clearTimeout);
    };
  }, [map, layoutReady, center[0], center[1], zoom]);
  return null;
}

/** Map click handler - tap map to move marker */
function MapClickHandler({
  onMapClick,
}: {
  onMapClick: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onMapClick(lat, lng);
    },
  });
  return null;
}

export function LocationPicker({
  initialAddress = '',
  initialLat,
  initialLng,
  onChange,
  onAddressChange,
  deferMapMs = 0,
}: LocationPickerProps) {
  const [address, setAddress] = useState(initialAddress);
  const initialLatN = parseCoord(initialLat);
  const initialLngN = parseCoord(initialLng);
  const [lat, setLat] = useState<number | null>(initialLatN);
  const [lng, setLng] = useState<number | null>(initialLngN);
  const [mapCenter, setMapCenter] = useState<[number, number]>(() =>
    toMapCenterTuple(initialLatN, initialLngN)
  );
  const [searchQuery, setSearchQuery] = useState(initialAddress);
  const [searchResults, setSearchResults] = useState<
    Array<{ formattedAddress: string; lat: number; lng: number }>
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const markerRef = useRef<L.Marker>(null);
  const searchDebounceRef = useRef<number | null>(null);
  const mapShellRef = useRef<HTMLDivElement | null>(null);
  const [mapVisible, setMapVisible] = useState(
    () => !(deferMapMs && deferMapMs > 0)
  );
  const [mapLayoutReady, setMapLayoutReady] = useState(false);

  useEffect(() => {
    if (!deferMapMs || deferMapMs <= 0) {
      setMapVisible(true);
      return;
    }
    setMapVisible(false);
    const t = window.setTimeout(() => {
      setMapVisible(true);
    }, deferMapMs);
    return () => clearTimeout(t);
  }, [deferMapMs]);

  useLayoutEffect(() => {
    if (!mapVisible) {
      setMapLayoutReady(false);
      return;
    }
    const el = mapShellRef.current;
    if (!el) {
      return;
    }
    const check = () => {
      const { width, height } = el.getBoundingClientRect();
      setMapLayoutReady(width >= MIN_MAP_PX && height >= MIN_MAP_PX);
    };
    const ro = new ResizeObserver(() => {
      check();
    });
    ro.observe(el);
    check();
    // Fallback: fixed-height shell should be ready on next frame
    const raf = requestAnimationFrame(check);
    const fallback = window.setTimeout(() => setMapLayoutReady(true), 600);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fallback);
      ro.disconnect();
      setMapLayoutReady(false);
    };
  }, [mapVisible]);

  useEffect(() => {
    if (initialAddress) {
      setAddress(initialAddress);
      setSearchQuery(initialAddress);
    }
    const ilat = parseCoord(initialLat);
    const ilng = parseCoord(initialLng);
    if (ilat != null) setLat(ilat);
    if (ilng != null) setLng(ilng);
    setMapCenter(prev => {
      const next = toMapCenterTuple(ilat, ilng);
      if (prev[0] === next[0] && prev[1] === next[1]) {
        return prev;
      }
      return next;
    });
  }, [initialAddress, initialLat, initialLng]);

  const notifyChange = useCallback(
    (addr: string, latitude: number | null, longitude: number | null) => {
      const p = toMarkerPosition(latitude, longitude);
      if (p) {
        onChange({ address: addr, lat: p[0], lng: p[1] });
      }
    },
    [onChange]
  );

  const updatePosition = useCallback(
    (newLat: number, newLng: number, newAddr?: string) => {
      if (!Number.isFinite(newLat) || !Number.isFinite(newLng)) {
        return;
      }
      setLat(newLat);
      setLng(newLng);
      setMapCenter([newLat, newLng]);
      const addr =
        newAddr ?? (address || 'Vị trí đã chọn (có thể sửa địa chỉ phía trên)');
      setAddress(addr);
      setSearchQuery(addr);
      notifyChange(addr, newLat, newLng);
    },
    [address, notifyChange]
  );

  const updatePositionWithReverseGeocode = useCallback(
    async (newLat: number, newLng: number) => {
      if (!Number.isFinite(newLat) || !Number.isFinite(newLng)) {
        return;
      }
      setLat(newLat);
      setLng(newLng);
      setMapCenter([newLat, newLng]);
      let addr = address || '';
      try {
        const res = await api.get<{
          success?: boolean;
          formattedAddress?: string;
          street?: string;
          houseNumber?: string;
          wardName?: string;
          districtName?: string;
          provinceName?: string;
        }>('/geocode/reverse', { params: { lat: newLat, lng: newLng } });
        const d = res.data;
        if (d?.success && d.formattedAddress) {
          addr = d.formattedAddress;
        } else if (
          d?.street ||
          d?.wardName ||
          d?.districtName ||
          d?.houseNumber
        ) {
          const streetPart =
            d.houseNumber &&
            d.street &&
            !String(d.street).trimStart().startsWith(String(d.houseNumber))
              ? `Số ${d.houseNumber}, ${d.street}`
              : d.houseNumber && !d.street
              ? `Số ${d.houseNumber}`
              : d.street;
          const parts = [
            streetPart,
            d.wardName,
            d.districtName,
            d.provinceName,
          ].filter(Boolean);
          addr = parts.join(', ');
        }
      } catch {
        addr = addr || `Lat: ${newLat.toFixed(6)}, Lng: ${newLng.toFixed(6)}`;
      }
      setAddress(addr);
      setSearchQuery(addr);
      notifyChange(addr, newLat, newLng);
    },
    [address, notifyChange]
  );

  const handleMapClick = useCallback(
    (mapLat: number, mapLng: number) => {
      updatePositionWithReverseGeocode(mapLat, mapLng);
    },
    [updatePositionWithReverseGeocode]
  );

  const handleMarkerDragEnd = useCallback(() => {
    const marker = markerRef.current;
    if (marker) {
      const pos = marker.getLatLng();
      updatePositionWithReverseGeocode(pos.lat, pos.lng);
    }
  }, [updatePositionWithReverseGeocode]);

  const searchAddress = useCallback(async (q: string) => {
    if (q.trim().length < 3) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await api.get('/geocode/search', { params: { q: q.trim() } });
      const data = res.data as {
        success?: boolean;
        results?: Array<{ formattedAddress: string; lat: number; lng: number }>;
      };
      const results = (data.results ?? []).slice(0, 5);
      setSearchResults(results);
      setShowSuggestions(results.length > 0);
    } catch {
      setSearchResults([]);
      setShowSuggestions(false);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const onSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setSearchQuery(v);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = window.setTimeout(
        () => searchAddress(v),
        400
      );
    },
    [searchAddress]
  );

  const onSelectSuggestion = useCallback(
    (item: { formattedAddress: string; lat: number; lng: number }) => {
      setSearchQuery(item.formattedAddress);
      setShowSuggestions(false);
      updatePosition(item.lat, item.lng, item.formattedAddress);
    },
    [updatePosition]
  );

  const eventHandlers = useMemo(
    () => ({
      dragend: handleMarkerDragEnd,
    }),
    [handleMarkerDragEnd]
  );

  const position: [number, number] | null = toMarkerPosition(lat, lng);

  const safeMapCenter = useMemo(
    () => toMapCenterTuple(mapCenter[0], mapCenter[1]),
    [mapCenter[0], mapCenter[1]]
  );
  const mapZoomInt = useMemo(() => (position ? 17 : 12), [position]);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: 8, position: 'relative' }}>
        <Input
          placeholder="Tìm kiếm địa chỉ (tối thiểu 3 ký tự)"
          value={searchQuery}
          onChange={onSearchInputChange}
          allowClear
          style={{ width: '100%' }}
          suffix={
            searchLoading ? (
              <span style={{ fontSize: 12 }}>Đang tìm...</span>
            ) : null
          }
        />
        {showSuggestions && searchResults.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 4,
              background: '#fff',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 1000,
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            {searchResults.map((r, i) => (
              <div
                key={i}
                role="button"
                tabIndex={0}
                onClick={() => onSelectSuggestion(r)}
                style={{
                  padding: '10px 12px',
                  cursor: 'pointer',
                  borderBottom:
                    i < searchResults.length - 1 ? '1px solid #f0f0f0' : 'none',
                  fontSize: 13,
                }}
                onKeyDown={e => e.key === 'Enter' && onSelectSuggestion(r)}
              >
                {r.formattedAddress}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        ref={mapShellRef}
        className="location-picker-map"
        style={mapShellStyle}
      >
        {!mapVisible || !mapLayoutReady ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(0,0,0,0.45)',
              fontSize: 13,
              border: '1px solid #f0f0f0',
              borderRadius: 4,
            }}
          >
            {!mapVisible ? 'Đang tải bản đồ…' : 'Đang căn layout bản đồ…'}
          </div>
        ) : (
          <MapContainer
            key={`${safeMapCenter[0].toFixed(4)}-${safeMapCenter[1].toFixed(
              4
            )}-${mapZoomInt}`}
            center={safeMapCenter}
            zoom={mapZoomInt}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <MapInvalidateOnMount layoutReady={mapLayoutReady} />
            <MapViewSync
              center={safeMapCenter}
              zoom={mapZoomInt}
              layoutReady={mapLayoutReady}
            />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url={cartoVoyagerLeafletTileUrl()}
              subdomains={['a', 'b', 'c', 'd']}
              maxZoom={20}
            />
            <MapClickHandler onMapClick={handleMapClick} />
            {position && (
              <Marker
                position={position}
                draggable
                eventHandlers={eventHandlers}
                ref={markerRef}
                icon={createIcon()}
              >
                <Popup>Kéo ghim để điều chỉnh vị trí quán</Popup>
              </Marker>
            )}
          </MapContainer>
        )}
      </div>
    </div>
  );
}

export default LocationPicker;
